/**
 * Adapta los lotes agrupados de una lista compartida a items de "listing" para el
 * share (Terrio y branded): una tarjeta por unidad funcional, y una por lote sin
 * unidades. La jerarquía es comercial (precio primero), no registral.
 *
 * "Mis listas" (UserLotListsPanel) sigue con el agrupado + acordeón de
 * ListDetailLotCard.
 */

import { getDisplayUnits } from './userLotUnits';
import { formatListingPrice, OPERATION_LABEL } from './listingCoreConstants';
import { resolveUnitOperation, mapOperationFromApi } from './userLotFormUtils';
import {
  buildLotContentListingMetas,
  buildUnitListingMetas,
  resolveLotContentHero,
  resolveLotContentTitle,
} from './listingPresentationUtils';
import {
  hasSharedLotUserContentDisplayData,
  LOT_CONTENT_OPERATION_LABEL,
} from './lotUserContentFormUtils';
import { resolveListingCoverImage } from './listingImageUtils';

export {
  buildListingMetas,
  buildLotContentListingMetas,
  buildUnitListingMetas,
  buildLotUserContentPresentation,
  resolveLotContentTitle,
} from './listingPresentationUtils';

const firstNonEmpty = (...values) => {
  const found = values.find((value) => {
    if (value === null || value === undefined) return false;
    return !(typeof value === 'string' && value.trim() === '');
  });
  return found === undefined ? null : found;
};

/** La valuación catastral se expresa en USD, igual que en el detalle del lote. */
const formatValuationAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount) || amount <= 0) return null;
  return `USD ${amount.toLocaleString('es-AR', { maximumFractionDigits: 0 })}`;
};

const resolveUnitTitle = (unit) => firstNonEmpty(unit?.title);

const resolveLotContentSubtitle = (content) => {
  const description = firstNonEmpty(content?.description, content?.notes);
  if (!description) return null;
  const normalized = String(description).replace(/\s+/g, ' ').trim();
  return normalized.length > 120 ? `${normalized.slice(0, 117)}…` : normalized;
};

/**
 * Monto destacado de la tarjeta. Prioridad: precio de la UF → contenido del lote
 * (lot_user_content) → valuación fiscal (etiquetada como tal).
 */
export const resolveListingHero = (unit, lot) => {
  const unitPriceLabel = formatListingPrice(unit?.price, unit?.currency);
  if (unitPriceLabel) {
    const operation = resolveUnitOperation(unit);
    return {
      kind: 'price',
      amount: unitPriceLabel,
      caption: (operation && OPERATION_LABEL[operation]) || 'Precio',
      source: 'unit',
    };
  }

  const lotContentHero = resolveLotContentHero(lot?.lot_user_content);
  if (lotContentHero) return lotContentHero;

  const valuation = formatValuationAmount(firstNonEmpty(
    unit?.valuation,
    unit?.lot_valuation,
    lot?.valuation,
    lot?.lot_valuation,
    lot?.filter_attributes?.valuation,
    lot?.filter_attributes?.lot_valuation,
  ));
  if (valuation) {
    return { kind: 'valuation', amount: valuation, caption: 'Valuación fiscal', source: 'valuation' };
  }

  return null;
};

export { resolveListingCoverImage } from './listingImageUtils';

const normalizeLocationKey = (value) => String(value)
  .trim()
  .toLowerCase()
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '');

const resolveStreetSegment = (...sources) => {
  const source = sources.find((candidate) => firstNonEmpty(
    candidate?.street_name,
    candidate?.street,
  ) != null);
  if (!source) return null;

  const street = String(firstNonEmpty(source.street_name, source.street)).trim();
  if (!street) return null;
  const number = firstNonEmpty(source.street_number);
  return number != null && String(number).trim() !== ''
    ? `${street} ${String(number).trim()}`
    : street;
};

/**
 * Ubicación de la card, del dato más específico al más general y sin repetir
 * (province y municipality suelen coincidir). Máximo 3 segmentos.
 */
export const buildListingLocationLabel = (lot, unit = null) => {
  const candidates = [
    resolveStreetSegment(unit, lot),
    firstNonEmpty(
      lot?.neighborhood_name, lot?.neighborhood,
      unit?.neighborhood_name, unit?.neighborhood,
    ),
    firstNonEmpty(
      lot?.locality_name, lot?.municipality_name,
      unit?.locality_name, unit?.municipality_name,
    ),
    firstNonEmpty(lot?.department_name, unit?.department_name),
    firstNonEmpty(lot?.province_name, unit?.province_name),
  ];

  const seen = new Set();
  const segments = [];
  candidates.forEach((candidate) => {
    if (candidate == null) return;
    const text = String(candidate).trim();
    if (!text) return;
    const key = normalizeLocationKey(text);
    if (!key || seen.has(key)) return;
    seen.add(key);
    segments.push(text);
  });

  return segments.slice(0, 3).join(' · ') || '—';
};

export const buildBrandedListingItems = (groupedLots) => {
  const items = [];

  (Array.isArray(groupedLots) ? groupedLots : []).forEach((lot) => {
    if (!lot?.lot_id) return;

    const shared = {
      lot,
      lotId: lot.lot_id,
      locationLabel: buildListingLocationLabel(lot),
      cadastralLabel: firstNonEmpty(lot.lot_name, lot.lot_id) ?? '—',
      hasLotContent: hasSharedLotUserContentDisplayData(lot?.lot_user_content),
    };

    const units = getDisplayUnits(lot);

    if (!units.length) {
      const lotContent = lot?.lot_user_content ?? null;
      const contentTitle = resolveLotContentTitle(lotContent);
      // Lote solo catastro (sin UF ni contenido privado): título = calle si viene
      // en el payload; no inventar nombre comercial.
      const cadastralTitle = contentTitle
        ? null
        : resolveStreetSegment(lot);
      items.push({
        ...shared,
        key: `lot:${lot.lot_id}`,
        itemType: 'lot',
        unit: null,
        userLotId: null,
        title: contentTitle || cadastralTitle,
        subtitle: resolveLotContentSubtitle(lotContent),
        hero: resolveListingHero(null, lot),
        metas: buildLotContentListingMetas(lot),
        coverImage: resolveListingCoverImage(lot, null),
      });
      return;
    }

    units.forEach((unit, index) => {
      const userLotId = unit?.user_lot_id ?? unit?.id ?? null;
      items.push({
        ...shared,
        key: `unit:${userLotId ?? `${lot.lot_id}:${index}`}`,
        itemType: 'unit',
        unit,
        userLotId,
        locationLabel: buildListingLocationLabel(lot, unit),
        title: resolveUnitTitle(unit),
        hero: resolveListingHero(unit, lot),
        metas: buildUnitListingMetas(unit, lot),
        coverImage: resolveListingCoverImage(lot, unit),
      });
    });
  });

  return items;
};

const normalizeSharedSearchTerm = (term) => String(term || '').trim().toLowerCase();

const publicationTextParts = (source) => {
  if (!source) return [];
  const attrs = source.private_attributes && typeof source.private_attributes === 'object'
    ? source.private_attributes
    : {};
  const flat = { ...source, ...attrs };
  const operation = mapOperationFromApi(flat);
  return [
    flat.title,
    flat.description,
    flat.notes,
    flat.observations,
    flat.unit_label,
    operation,
    operation ? (OPERATION_LABEL[operation] || LOT_CONTENT_OPERATION_LABEL[operation]) : null,
    formatListingPrice(flat.price, flat.currency),
    flat.currency,
    flat.price != null && flat.price !== '' ? String(flat.price) : null,
  ].filter(Boolean);
};

/**
 * Coincidencia de búsqueda sobre los mismos campos que muestra la card branded
 * (título, subtítulo, ubicación, catastral, precio/operación, metas y lot_user_content).
 */
export const sharedListingItemMatchesSearch = (item, term) => {
  const normalized = normalizeSharedSearchTerm(term);
  if (!normalized) return true;
  if (!item) return false;

  const lotPublicationParts = [
    ...publicationTextParts(item.lot?.lot_user_content),
    ...publicationTextParts(item.lot),
  ];

  const haystack = [
    ...lotPublicationParts,
    item.title,
    item.subtitle,
    item.locationLabel,
    item.cadastralLabel,
    item.hero?.amount,
    item.hero?.caption,
    ...(Array.isArray(item.metas) ? item.metas.flatMap((meta) => [meta.label, meta.value]) : []),
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  return haystack.includes(normalized);
};

/** Filtro de operación en vistas compartidas: UFs y lotes solo con lot_user_content. */
export const filterGroupedLotBySharedOperation = (lot, operationFilter) => {
  if (!operationFilter) return lot;
  if (!lot) return null;

  const units = getDisplayUnits(lot);
  if (units.length) {
    const filteredUnits = units.filter(
      (unit) => resolveUnitOperation(unit) === operationFilter,
    );
    if (!filteredUnits.length) return null;
    return { ...lot, units: filteredUnits, unit_count: filteredUnits.length };
  }

  const lotContentOperation = mapOperationFromApi(lot?.lot_user_content);
  if (lotContentOperation !== operationFilter) return null;
  return lot;
};

/**
 * Filtra un lote agrupado para vistas compartidas: sin unit_label; solo datos
 * visibles en la card. Si hay UFs, devuelve solo las que matchean el término.
 */
export const filterGroupedLotForSharedSearch = (lot, term) => {
  const normalized = normalizeSharedSearchTerm(term);
  if (!normalized) return lot;
  if (!lot) return null;

  const items = buildBrandedListingItems([lot]);
  const matchingItems = items.filter((item) => sharedListingItemMatchesSearch(item, normalized));
  if (!matchingItems.length) return null;

  const units = getDisplayUnits(lot);
  if (!units.length) return lot;

  const unitItems = items.filter((item) => item.itemType === 'unit');
  const matchingUnitIds = new Set(
    matchingItems
      .filter((item) => item.itemType === 'unit' && item.userLotId != null)
      .map((item) => String(item.userLotId)),
  );

  if (!matchingUnitIds.size || matchingUnitIds.size === unitItems.length) {
    return lot;
  }

  const filteredUnits = units.filter((unit) =>
    matchingUnitIds.has(String(unit.user_lot_id ?? unit.id)),
  );
  if (!filteredUnits.length) return lot;

  return { ...lot, units: filteredUnits, unit_count: filteredUnits.length };
};

export const sharedLotMatchesSearch = (lot, term) =>
  filterGroupedLotForSharedSearch(lot, term) != null;
